import React, {useMemo, useState} from 'react'
import axios from 'axios'
import Select from 'react-select'
import {Button, Form} from 'react-bootstrap'
import {Form as FormikForm, Formik} from 'formik'
import moment from 'moment'
import { KTSVG } from '../../../../../_metronic/helpers'
import { useInterpreters, useLanguages } from '../../../../hooks/useGlobalData'
import toaster from '../../../../../Utils/toaster'

const API_URL = process.env.REACT_APP_API_URL

type Props = {
  show: boolean
  onClose: () => void
  canEdit: boolean
  callDetails: any
  onRefresh?: () => void
  onSaved?: (next: {
    interpreterUserId?: string
    code?: string
    interpreterLabel?: string
    languageCode?: string
    languageLabel?: string
    startTime?: string
    endTime?: string
    startTimeIso?: string | null
    endTimeIso?: string | null
  }) => void
}

const EditInterpreterLanguageModal: React.FC<Props> = ({show, onClose, canEdit, callDetails, onRefresh, onSaved}) => {
  const [loading, setLoading] = useState(false)

  // Use global cached data for interpreters and languages
  const {data: interpretersData} = useInterpreters()
  const {data: languagesData} = useLanguages()
  
  const interpreterOptions = useMemo(() => 
    (interpretersData ?? []).map((d: any) => ({value: d.key ?? '', label: d.value ?? ''}))
  , [interpretersData])
  
  const languageOptions = useMemo(() => 
    (languagesData ?? []).map((d: any) => ({value: d.key ?? '', label: d.value ?? ''}))
  , [languagesData])

  const toHHmmss = (val: any) => {
    if (!val || val === 'N/A') return ''
    const m = moment(val, ['M/D/YYYY h:mm:ss A', moment.ISO_8601], true)
    if (m.isValid()) return m.format('HH:mm:ss')
    const mFallback = moment(val)
    if (mFallback.isValid()) return mFallback.format('HH:mm:ss')
    return ''
  }

  const baseDate = useMemo(() => {
    const raw =
      callDetails?.connectedToAgentTimestamp &&
      callDetails.connectedToAgentTimestamp !== 'N/A'
        ? callDetails.connectedToAgentTimestamp
        : callDetails?.initiationTimestamp ?? callDetails?.agentTimestamp ?? null

    if (!raw) return null

    const m = moment(raw, ['M/D/YYYY h:mm:ss A', moment.ISO_8601], true)
    if (m.isValid()) return m

    const mFallback = moment(raw)
    return mFallback.isValid() ? mFallback : null
  }, [callDetails])
 
  const defaultInterpreterUserId = useMemo(() => {
    if (interpreterOptions.length === 0) return ''
    const id = callDetails?.assignedInterpreterId
      ? String(callDetails.assignedInterpreterId)
      : ''
    if (id && interpreterOptions.find((o: any) => o.value === id)) return id
    const nameToMatch =
      callDetails?.assignedInterpreter || callDetails?.interpreterNameInline || ''
    if (!nameToMatch) return ''
    const byName = interpreterOptions.find((o: any) => {
      const labelName = o.label?.split(' - ')[0]?.trim().toLowerCase()
      return labelName === nameToMatch.trim().toLowerCase()
    })
    return byName?.value ?? ''
  }, [callDetails, interpreterOptions])

  const defaultLanguageCode = useMemo(() => {
    const languageLabel = callDetails?.requester?.language || callDetails?.languages
    if (!languageLabel || languageOptions.length === 0) return ''
    const matchedOption = languageOptions.find(
      (opt: any) =>
        opt.label?.toString().trim().toLowerCase() ===
        languageLabel?.toString().trim().toLowerCase()
    )

    return matchedOption?.value ?? ''
  }, [callDetails, languageOptions])

  const toIsoOnBase = (timeHHmmss: string) => {
    
    if (!timeHHmmss || !baseDate || !baseDate.isValid()) return null
    const [hh, mm, ss] = timeHHmmss.split(':')
    // Create a new moment with same date as baseDate but new time
    const d = baseDate.clone().set({
      hour: Number(hh || 0),
      minute: Number(mm || 0),
      second: Number(ss || 0),
    })
    // Format as ISO 8601 without timezone conversion: "YYYY-MM-DDTHH:mm:ss"
    return d.format('YYYY-MM-DDTHH:mm:ss')
  }

  const toSeconds = (time: any) => {
    if (!time) return null
    const m = moment(String(time), ['HH:mm:ss', 'HH:mm'], true)
    if (!m.isValid()) return null
    return m.hours() * 3600 + m.minutes() * 60 + m.seconds()
  }

  // Data is now loaded from global hooks - no need for manual loading

  const isVoucherCreated = callDetails?.isVoucherCreated ?? false
  const isInvoiceCreated = callDetails?.isInvoiceCreated ?? false
  const hasLinkedBilling = isVoucherCreated || isInvoiceCreated

  const billingWarning = useMemo(() => {
    if (!hasLinkedBilling) return ''
    const parts: string[] = []
    if (isInvoiceCreated) parts.push('an invoice')
    if (isVoucherCreated) parts.push('a voucher')
    return `Call is already part of ${parts.join(' and ')}. Please unlink the line first.`
  }, [isInvoiceCreated, isVoucherCreated, hasLinkedBilling])

  if (!show) return null

  // Show warning immediately when modal opens if invoice/voucher exists
  if (hasLinkedBilling) {
    return (
      <div className='call-details-edit-modal border rounded bg-white p-4 shadow-lg' style={{ width: '100%'}}>
        <div className='d-flex align-items-center justify-content-between mb-4 border-bottom pb-3'>
          <div className='fs-4 fw-bold'>Cannot Edit</div>
          <Button variant='link' onClick={onClose} className='p-0 text-decoration-none'>
            <KTSVG
              path='/media/icons/duotune/arrows/arr061.svg'
              className='svg-icon svg-icon-2x'
            />
          </Button>
        </div>
        <p className='mb-4'>{billingWarning}</p>
        <div className='d-flex justify-content-end pt-2 border-top'>
          <Button variant='primary' onClick={onClose}>OK</Button>
        </div>
      </div>
    )
  }

  return (
    <div className='call-details-edit-modal border rounded bg-white p-4 shadow-lg' style={{ width: '100%'}}>
      <div className='d-flex align-items-center justify-content-between mb-4 border-bottom pb-3'>
        <div className='fs-4 fw-bold'>Edit Details</div>
        <Button variant='link' onClick={onClose} disabled={loading} className='p-0 text-decoration-none'>
         <KTSVG
                  path='/media/icons/duotune/arrows/arr061.svg'
                  className='svg-icon svg-icon-2x'
                />
        </Button>
        
      </div>

      <Formik
        initialValues={{
          interpreterUserId: defaultInterpreterUserId,
          languageCode: defaultLanguageCode,
          startTime: toHHmmss(callDetails?.connectedToAgentTimestamp),
          endTime: toHHmmss(callDetails?.disconnectTimestamp),
        }}
        enableReinitialize
        validate={(values) => {
          const errors: any = {}
          const startSec = toSeconds(values.startTime)
          const endSec = toSeconds(values.endTime)

          // Only validate ordering when both are present & valid time strings
          if (startSec !== null && endSec !== null && endSec < startSec) {
            errors.endTime = 'End time must be greater than or equal to start time.'
          }

          return errors
        }}
        onSubmit={async (values) => {
          if (!callDetails?.id) return

          setLoading(true)
          try {
            const selectedInterpreter = interpreterOptions.find((o: any) => o.value === values.interpreterUserId)
            const selectedLanguage = languageOptions.find((o: any) => o.value === values.languageCode)

            const startTimeIso = values.startTime ? toIsoOnBase(values.startTime) : null
            const endTimeIso = values.endTime ? toIsoOnBase(values.endTime) : null

            const payload: any = {Code: callDetails.id}
            if (values.interpreterUserId) payload.InterpreterUserId = values.interpreterUserId
            if (values.languageCode) payload.LanguageTo = values.languageCode
            if (startTimeIso) payload.StartTime = startTimeIso
            if (endTimeIso) payload.EndTime = endTimeIso

            await axios.put(`${API_URL}/Appoinment/update-interpreter-language`, payload)

            onSaved?.({
              interpreterUserId: values.interpreterUserId,
              interpreterLabel: selectedInterpreter?.label,
              languageCode: values.languageCode,
              languageLabel: selectedLanguage?.label,
              startTime: values.startTime,
              endTime: values.endTime,
              startTimeIso,
              endTimeIso,
            })

            onRefresh?.()
            onClose()
          } catch (e: any) {
            toaster('error', e?.response?.data?.text ?? e?.response?.data?.Text ?? 'Failed to update call log')
          } finally {
            setLoading(false)
          }
        }}
      >
        {({values, setFieldValue, handleSubmit, getFieldProps, errors, touched, isValid}) => (
          <FormikForm onSubmit={handleSubmit}>
            <div className='row g-4'>
              <div className='col-12 col-lg-6'>
                <Form.Group className='mb-0'>
                  <Form.Label htmlFor='interpreterUserId'>Interpreter</Form.Label>
                  <Select
                    className='react-select-styled react-select-solid react-select-sm'
                    classNamePrefix='react-select'
                    name='interpreterUserId'
                    inputId='interpreterUserId'
                    options={interpreterOptions}
                    isMulti={false}
                    placeholder='Select Interpreter'
                    value={interpreterOptions.find((o: any) => o.value === values.interpreterUserId) || null}
                    onChange={(opt: any) => setFieldValue('interpreterUserId', String(opt?.value ?? ''))}
                  />
                </Form.Group>
              </div>

              <div className='col-12 col-lg-6'>
                <Form.Group className='mb-0'>
                  <Form.Label>Language</Form.Label>
                  <Select
                    className='react-select-styled react-select-solid react-select-sm'
                    classNamePrefix='react-select'
                    options={languageOptions}
                    isMulti={false}
                    placeholder='Select Language'
                    value={languageOptions.find((o: any) => o.value === values.languageCode) || null}
                    onChange={(opt: any) => setFieldValue('languageCode', String(opt?.value ?? ''))}
                  />
                </Form.Group>
              </div>

              <div className='col-12 col-lg-6'>
                <Form.Group className='mb-0'>
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type='time'
                    step={1}
                    {...getFieldProps('startTime')}
                    className='form-control-sm'
                    isInvalid={!!touched.startTime && !!errors.startTime}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.startTime as any}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className='col-12 col-lg-6'>
                <Form.Group className='mb-0'>
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type='time'
                    step={1}
                    {...getFieldProps('endTime')}
                    className='form-control-sm'
                    isInvalid={!!touched.endTime && !!errors.endTime}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.endTime as any}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className='col-12'>
                <div className='d-flex justify-content-end gap-2 pt-2 border-top'>
                  <Button variant='secondary' onClick={onClose} disabled={loading}>
                    Cancel
                  </Button>
                  {canEdit && (
                    <Button variant='primary' type='submit' disabled={loading || !isValid}>
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </FormikForm>
        )}
      </Formik>


    </div>
  )
}

export default EditInterpreterLanguageModal
